import React, { useState } from 'react';
import apiClient from '../api/apiClient';
import { ReactComponent as EyeOpenIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeClosedIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as RefreshIcon } from '../assets/icons/refresh.svg';

// Function to generate a strong password
const generateStrongPassword = () => {
  const length = 12;
  const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
  const numberChars = '0123456789';
  const symbolChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  
  const allChars = uppercaseChars + lowercaseChars + numberChars + symbolChars;
  
  const getRandomChar = (charSet) => {
    return charSet[Math.floor(Math.random() * charSet.length)];
  };
  
  // Ensure at least one character from each set
  const password = [
    getRandomChar(uppercaseChars),
    getRandomChar(lowercaseChars),
    getRandomChar(numberChars),
    getRandomChar(symbolChars)
  ];
  
  // Fill the rest of the password
  while (password.length < length) {
    password.push(getRandomChar(allChars));
  }
  
  // Shuffle the password
  for (let i = password.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [password[i], password[j]] = [password[j], password[i]];
  }
  
  return password.join('');
};

const PortalUserModal = ({ user, onClose, onSave }) => {
  const isNew = !user;
  
  const [formData, setFormData] = useState({
    username: user?.username || '',
    role: user?.role || 'member',
    is_active: user?.is_active ?? true
  });
  
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const generatePassword = () => {
    const newPassword = generateStrongPassword();
    setPassword(newPassword);
  };
  
  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    // Optionally, you could show a tooltip or temporary message
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (isNew && !password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    const userData = {
      ...formData
    };
    
    // Only include password for new users or when it's changed
    if (isNew || password) {
      userData.password = password;
    }
    
    try {
      setIsLoading(true);
      await onSave(userData, isNew);
    } catch (error) {
      console.error('Error saving portal user:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {isNew ? 'Add New Portal User' : 'Edit Portal User'}
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                  errors.username ? 'border-red-500' : 'border-gray-300'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-500">{errors.username}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            
            {/* Password Section - Only for New Users or Password Reset */}
            {isNew && (
              <div className="sm:col-span-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    className={`w-full px-3 py-2 pr-24 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-500 hover:text-gray-700"
                      title={showPassword ? 'Hide Password' : 'Show Password'}
                    >
                      {showPassword ? <EyeOpenIcon className="w-5 h-5" /> : <EyeClosedIcon className="w-5 h-5" />}
                    </button>
                    <button
                      type="button"
                      onClick={generatePassword}
                      className="text-primary-600 hover:text-primary-700"
                      title="Generate Password"
                    >
                      <RefreshIcon className="w-5 h-5" />
                    </button>
                    {password && (
                      <button
                        type="button"
                        onClick={copyPassword}
                        className="text-green-600 hover:text-green-700"
                        title="Copy Password"
                      >
                        <CopyIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>
            )}
            
            <div className="sm:col-span-2">
              <label htmlFor="is_active" className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={isLoading}
                  className={`h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                />
                <span className="ml-2 text-sm text-gray-700">
                  Active User
                </span>
              </label>
            </div>
          </div>
          
          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center justify-center ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                isNew ? 'Create User' : 'Update User'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PortalUserModal